import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import './GuestSignInBar.css';

const DISMISS_KEY = 'rk-guest-popup-dismiss';

function wasDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function CloseX() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default function GuestSignInBar() {
  const { user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (user || wasDismissed()) return undefined;
    const tmr = setTimeout(() => setOpen(true), 1400);
    return () => clearTimeout(tmr);
  }, [user]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const dismiss = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  if (user || !open) return null;

  const loginHref = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;

  return createPortal(
    <div className="guest-popup" role="presentation">
      <button type="button" className="guest-popup__backdrop" aria-label={t.guestBarLater} onClick={dismiss} />
      <div
        ref={panelRef}
        className="guest-popup__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-popup-title"
        tabIndex={-1}
      >
        <div className="guest-popup__header">
          <button type="button" className="guest-popup__close" onClick={dismiss} aria-label={t.closeBtn}>
            <CloseX />
          </button>
          <img src="/rk-logo.webp" alt="" className="guest-popup__logo" />
          <h2 id="guest-popup-title" className="guest-popup__title">
            {t.guestBarTitle}
          </h2>
          <p className="guest-popup__sub">{t.guestBarSub}</p>
        </div>

        <div className="guest-popup__body">
          <a href={loginHref} className="guest-popup__cta">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t.guestBarBtn}
          </a>
          <button type="button" className="guest-popup__later" onClick={dismiss}>
            {t.guestBarLater}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
